import {
	GlobalSearchScopeIdentifier,
	GlobalSearchScopeKind,
	IGlobalSearchResultItem,
	SearchIntegrationKind
} from "client/Api/TitleBar/GlobalSearch/globalSearch";
import { GlobalSearchItemPersistedModel } from "./models";
import createVanilla, { StoreApi } from "zustand/vanilla";
import { persist } from "zustand/middleware";
import {
	deserializeState,
	SafeStateStorage,
	serializeState
} from "./storage";
import create, { UseStore } from "zustand";
import { SetStateAction, useCallback } from "react";
import { convertForPersistence } from "./convertForPersistence";
import { areTheSame } from "./compare";
import { withMigrator } from "client/zustand/migrations";

const MAX_NUMBER_OF_PERSISTED_ITEMS = 5;

export type UseRecentlyOpenedResultsHandlers = {
	register: (
		item: IGlobalSearchResultItem,
		itemScope:
			| GlobalSearchScopeIdentifier
			| SearchIntegrationKind
	) => void;
	registerFromRecentlyOpened: (item: GlobalSearchItemPersistedModel) => void;
	remove: (item: GlobalSearchItemPersistedModel) => void;
	clear: () => void;
	items: readonly GlobalSearchItemPersistedModel[];
};

export function useRecentlyOpenedResults(): UseRecentlyOpenedResultsHandlers {
	const {
		items,
		setItems
	} = useStore();

	const registerHandler = useCallback(
		(item: GlobalSearchItemPersistedModel): void => {
			setItems(
				items => {
					const itemsWithoutDuplicate: GlobalSearchItemPersistedModel[] = items.filter(i => !areTheSame(i, item));

					let updatedItems: GlobalSearchItemPersistedModel[] = [
						item,
						...itemsWithoutDuplicate
					];

					if (updatedItems.length > MAX_NUMBER_OF_PERSISTED_ITEMS) {
						updatedItems = updatedItems.slice(0, MAX_NUMBER_OF_PERSISTED_ITEMS);
					}

					return updatedItems;
				}
			);
		},
		[setItems]
	);

	const registerFromSearchHandler = useCallback(
		(
			newItem: IGlobalSearchResultItem,
			itemScope:
				| GlobalSearchScopeIdentifier
				| SearchIntegrationKind
		): void => {
			if (typeof itemScope === "number") {
				itemScope = {
					kind: GlobalSearchScopeKind.Predefined,
					predefinedKind: itemScope
				};
			}

			const itemToPersist: GlobalSearchItemPersistedModel = convertForPersistence(
				newItem,
				itemScope
			);

			registerHandler(itemToPersist);
		},
		[registerHandler]
	);

	const removeHandler = useCallback(
		(item: GlobalSearchItemPersistedModel): void => {
			setItems(
				items => items.filter(i => !areTheSame(i, item))
			);
		},
		[setItems]
	);

	const clearHandler = useCallback(
		(): void => {
			setItems([]);
		},
		[setItems]
	);

	return {
		items,
		register: registerFromSearchHandler,
		registerFromRecentlyOpened: registerHandler,
		remove: removeHandler,
		clear: clearHandler
	};
}

export type RecentlyOpenedResultsStoreData = {
	items: readonly GlobalSearchItemPersistedModel[];
	setItems: (items: SetStateAction<readonly GlobalSearchItemPersistedModel[]>) => void;
};

const store: StoreApi<RecentlyOpenedResultsStoreData> = createVanilla<RecentlyOpenedResultsStoreData>(
	persist(
		(set) => {
			return {
				items: [],
				setItems: (items: SetStateAction<readonly GlobalSearchItemPersistedModel[]>): void => {
					if (typeof items === "function") {
						set(previous => ({
							...previous,
							items: items(previous.items)
						}));
					} else {
						set({ items });
					}
				}
			};
		},
		withMigrator({
			name: "RecentlyOpenedSearchItems",
			getStorage: () => new SafeStateStorage(),
			serialize: serializeState,
			deserialize: async (str) => await deserializeState(str)
		})
	)
);

const useStore: UseStore<RecentlyOpenedResultsStoreData> = create(store);